// ============================================================
//  ADMIN MARK DONE PANEL — ADMIN DASHBOARD
//  Path: src/components/AdminMarkDonePanel.tsx
//
//  NEW FILE. Shown on an ACTIVE (assigned) task. For a tasker who
//  forgot to mark the job done, or couldn't (broken phone, app issue,
//  …). Does exactly what the tasker's own "Mark as Done" does: the job
//  moves to "awaiting the customer's confirmation" and the customer
//  still confirms or disputes (or it auto-confirms). It does NOT
//  complete the job or pay the tasker by itself — only a dispute
//  decision does that.
//
//  Skips the "steps must be tapped" rule (that is its purpose). Refused
//  by the server if the tasker's payout setup is incomplete. Recorded in
//  the audit log and the task timeline with the admin's note.
//  super_admin / admin only (same as the backend route).
// ============================================================

import React, { useState } from 'react'
import { markTaskDoneForTasker } from '../services/admin.service'

const MAX_NOTE_LENGTH = 1000

const AdminMarkDonePanel: React.FC<{
  taskId:    string
  canAct:    boolean        // super_admin / admin
  onDone:    () => void     // reload the task
}> = ({ taskId, canAct, onDone }) => {
  const [open, setOpen]   = useState(false)
  const [note, setNote]   = useState('')
  const [busy, setBusy]   = useState(false)
  const [error, setError] = useState('')
  const [done, setDone]   = useState('')

  if (!canAct) return null

  const submit = async () => {
    if (!window.confirm(
      "Mark this job as done on the tasker's behalf?\n\n" +
      'The customer will be asked to confirm or dispute, exactly as if the tasker had tapped Mark as Done.'
    )) return
    setBusy(true)
    setError('')
    try {
      const res = await markTaskDoneForTasker(taskId, { note: note.trim() || undefined })
      setDone(res?.data?.message || "Marked as done — waiting for the customer to confirm")
      onDone()
    } catch (err: any) {
      console.error('Failed to mark task done for tasker:', err)
      setError(err?.response?.data?.message || (err?.isNetworkError
        ? 'Could not reach the server — check your connection and try again.'
        : 'Could not mark this job as done.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mb-4 bg-surface-tertiary border border-border-light rounded-xl p-3">
      <p className="text-xs text-gray-400 uppercase font-semibold mb-1">✅ Mark as Done for the tasker</p>
      <p className="text-xs text-gray-500 mb-2">
        For a tasker who forgot or couldn't mark it done. The customer still confirms or disputes.
      </p>
      {done ? (
        <p className="text-sm text-green-400">{done}</p>
      ) : !open ? (
        <button onClick={() => setOpen(true)}
          className="px-3 py-1.5 text-xs bg-brand-600/20 text-brand-400 rounded-lg hover:bg-brand-600/30 transition-colors">
          Mark as Done on the tasker's behalf…
        </button>
      ) : (
        <>
          <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, MAX_NOTE_LENGTH))} rows={2}
            placeholder="Why? e.g. tasker's phone broke — confirmed by phone call (saved in the audit log)"
            className="w-full bg-surface-secondary border border-border-light rounded-lg px-3 py-2 text-white text-xs mb-2 focus:border-brand-500 focus:outline-none resize-none" />
          <div className="flex gap-2">
            <button onClick={submit} disabled={busy}
              className="px-3 py-1.5 text-xs bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors">
              {busy ? 'Working…' : 'Mark as Done'}
            </button>
            <button onClick={() => { setOpen(false); setError('') }} disabled={busy}
              className="px-3 py-1.5 text-xs bg-surface-secondary text-gray-400 rounded-lg hover:text-white transition-colors">
              Cancel
            </button>
          </div>
          {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
        </>
      )}
    </div>
  )
}

export default AdminMarkDonePanel